import { Dictionary } from 'lodash';
import groupBy from 'lodash/groupBy';
import moment from 'moment';
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import InView from 'react-intersection-observer';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { BaseComment, Comment, MessageType } from 'api/conversations';
import { Reaction, UserMessage } from 'api/conversations/types';
import { User } from 'api/models';
import { ConversationMember } from 'api/project-conversations';
import { FloatingDate } from 'components/atoms/date-separator';
import { LogType, useLoadLoggerForDataDog } from 'hooks/use-data-dog-logger';
import { InitRouter } from 'initRouter';
import { useUserTimeoffLookup } from 'queries/members';
import { queueMacrotask } from 'utils/macrotask';
import { unreachable } from 'utils/unreachable';
import { ConversationsEmptyState, ConversationsIcon } from './empty-state';
import {
  ControlMessageWrapper,
  EndMarker,
  MessageListWrapper,
  MessageWrapper,
  SeenBy,
  StartMarker,
  StyledEmpty,
  UserMessageWrapper,
} from './messages-list.style';

const PlaceHolderToFixScrollBounce = styled.div`
  width: 100%;
  color: transparent;
`;

type MessagesListProps = {
  messages: Comment[];
  lastReadComment?: Comment;
  scrollToEnd?: boolean;
  messagesInitialComponent?: ReactNode;
  renderMessageFooterExtra?: (comment: Comment) => ReactNode;
  renderMessageTailComponent?: (
    comment: Comment,
    lastSeenUsers?: ConversationMember[],
  ) => ReactNode;
  onAddReactionToComment?: (commentId, reactionType) => void;
  onCommentUpdate?: ({ commentId, content }: UserMessage) => void;
  onPerformReply?: (commentId: number) => void;
  onPerformDelete?: (commentId: number) => void;
  empty?: ReactNode;
  onSeen?: (comment: BaseComment) => void;
  disallowMentions?: boolean;
  messagesThatNeedUpdate?: number[];
  lastSeenUsersForComments?: Record<string, ConversationMember[]>;
};

const NewMessageComponent = ({
  message,
  userLookup,
  previousMessage,
  onAddReactionToComment,
  onCommentUpdate,
  onPerformReply,
  onPerformDelete,
  disallowMentions,
}) => {
  switch (message.messageType) {
    case MessageType.ControlMessage:
      return <ControlMessageWrapper comment={message} />;
    case MessageType.UserMessage:
      if (
        onAddReactionToComment &&
        onCommentUpdate &&
        onPerformReply &&
        onPerformDelete
      ) {
        return (
          <UserMessageWrapper
            comment={message}
            prevComment={
              previousMessage?.messageType === MessageType.UserMessage
                ? previousMessage
                : undefined
            }
            onAddReactionToComment={onAddReactionToComment}
            onCommentUpdate={onCommentUpdate}
            onPerformReply={onPerformReply}
            onPerformDelete={onPerformDelete}
            disallowMentions={disallowMentions}
            userLookup={userLookup}
          />
        );
      }

      return null;
    default:
      return unreachable(message as never);
  }
};

const GroupedMessageContainer = ({
  message,
  onSeen,
  userLookUp,
  prevMessage,
  onAddReactionToComment,
  onCommentUpdate,
  onPerformDelete,
  onPerformReply,
  disallowMentions,
  renderMessageTailComponent,
  renderMessageFooterExtra,
  lastSeenUsers,
}: {
  message: Comment;
  onSeen?: (comment: BaseComment) => void;
  userLookUp: Dictionary<User>;
  prevMessage?: Comment;
  onAddReactionToComment?: (commentId: number, reactionType: Reaction) => void;
  onCommentUpdate?: ({ commentId, content }: UserMessage) => void;
  onPerformDelete?: (commentId: number) => void;
  onPerformReply?: (commentId: number) => void;
  disallowMentions?: boolean;
  renderMessageFooterExtra?: (comment: Comment) => ReactNode;
  renderMessageTailComponent?: (
    comment: Comment,
    lastSeenUsers?: ConversationMember[],
  ) => ReactNode;
  lastSeenUsers?: ConversationMember[];
}) => {
  return (
    <InView
      skip={!onSeen}
      onChange={(inView) => {
        if (inView) {
          onSeen?.(message);
        }
      }}
      key={message.commentGuid}
    >
      <MessageWrapper>
        <NewMessageComponent
          message={message}
          userLookup={userLookUp}
          previousMessage={prevMessage}
          onAddReactionToComment={onAddReactionToComment}
          onCommentUpdate={onCommentUpdate}
          onPerformDelete={onPerformDelete}
          onPerformReply={onPerformReply}
          disallowMentions={disallowMentions}
        />
        {message.messageType === MessageType.UserMessage ? (
          <>
            {renderMessageTailComponent && (
              <SeenBy>
                {renderMessageTailComponent(message, lastSeenUsers)}
              </SeenBy>
            )}
            {renderMessageFooterExtra && renderMessageFooterExtra(message)}
          </>
        ) : null}
      </MessageWrapper>
    </InView>
  );
};

const MemoedGroupedMessageContainer = React.memo(GroupedMessageContainer);

export const MessagesList: FC<MessagesListProps> = ({
  messages,
  scrollToEnd,
  messagesInitialComponent,
  renderMessageFooterExtra,
  renderMessageTailComponent,
  onAddReactionToComment,
  onCommentUpdate,
  onPerformDelete,
  onPerformReply,
  onSeen,
  empty = <ConversationsEmptyState />,
  disallowMentions = false,
  lastSeenUsersForComments,
}) => {
  const bottomContainerRef = useRef<HTMLDivElement>(null);
  // const [groupedMessages, setGroupedMessages] = useState({});
  const [canMessageListAutoScroll, setMessageListAutoScroll] = useState(true);
  const scrollToEndOfCommentList = useCallback(
    () =>
      (canMessageListAutoScroll || scrollToEnd) &&
      bottomContainerRef.current?.scrollIntoView(),
    [canMessageListAutoScroll, scrollToEnd],
  );

  const { pathname } = useLocation();

  useLoadLoggerForDataDog(
    InitRouter.getInstance().tagNames(pathname),
    LogType.AutoLog,
    true,
  );

  useEffect(() => {
    queueMacrotask(() => scrollToEndOfCommentList());
  }, [scrollToEndOfCommentList, messages]);

  // useEffect(() => {
  //   setGroupedMessages(
  //     groupBy(messages, (message) => moment(message.createdAt).startOf('day')),
  //   );
  // }, [messages, setGroupedMessages]);

  const groupedMessages = useMemo(
    () =>
      groupBy(messages, (message) => moment(message.createdAt).startOf('day')),
    [messages],
  );

  const { data: userLookUp = {} } = useUserTimeoffLookup();

  return (
    <MessageListWrapper>
      <StartMarker />
      {messages.length > 0 ? (
        <>
          {messagesInitialComponent ?? <ConversationsIcon />}
          {Object.keys(groupedMessages).map((groupKey) => (
            <div key={groupKey} style={{ overflowAnchor: 'none' }}>
              <FloatingDate timestamp={moment(groupKey)} />
              {groupedMessages[groupKey].map(
                (message: Comment, index: number) => (
                  <MemoedGroupedMessageContainer
                    key={`${message.commentId}-${message.createdAt}`}
                    message={message}
                    onSeen={onSeen}
                    userLookUp={userLookUp}
                    prevMessage={groupedMessages[groupKey][index - 1]}
                    onAddReactionToComment={onAddReactionToComment}
                    onCommentUpdate={onCommentUpdate}
                    onPerformDelete={onPerformDelete}
                    onPerformReply={onPerformReply}
                    disallowMentions={disallowMentions}
                    renderMessageTailComponent={renderMessageTailComponent}
                    renderMessageFooterExtra={renderMessageFooterExtra}
                    lastSeenUsers={
                      lastSeenUsersForComments?.[message.commentId]
                    }
                  />
                ),
              )}
            </div>
          ))}
          <PlaceHolderToFixScrollBounce>-</PlaceHolderToFixScrollBounce>
        </>
      ) : (
        messagesInitialComponent ?? <StyledEmpty>{empty}</StyledEmpty>
      )}
      <InView
        onChange={(inView) => setMessageListAutoScroll(inView)}
        as="div"
        style={{ overflowAnchor: 'auto', height: '1px' }}
      >
        <EndMarker ref={bottomContainerRef} />
      </InView>
    </MessageListWrapper>
  );
};
